// filepath: apps/web/src/features/admin/audit/AuditViewerPage.tsx
//
// FR-UI-ADM-14 — Top-level page that orchestrates FilterBar + Table + Pagination.
//
// FR-UI-CORE-10: 5-state machine + 6th validation-error.
// AL-1..15: filters, pagination, RFC3339 client-side validation.

import { useState } from "react";
import { useTenant } from "@/tenant/TenantProvider";
import { useTranslation } from "@/i18n/useTranslation";
import { useAuditLog } from "./queries";
import { AuditFilterBar } from "./AuditFilterBar";
import { AuditViewerTable } from "./AuditViewerTable";
import { AuditPagination } from "./AuditPagination";
import { emptyFilter, type AuditFilterState } from "./types";

export interface AuditViewerPageProps {
  locale: string;
}

export function AuditViewerPage({ locale: _locale }: AuditViewerPageProps) {
  const tenantSlug = useTenant().tenantSlug;
  const { t } = useTranslation();
  const [filter, setFilter] = useState<AuditFilterState>(emptyFilter());
  const { data, state, error } = useAuditLog(tenantSlug, filter);

  switch (state) {
    case "loading":
      return (
        <p className="audit-viewer__loading">{t("audit.state.loading")}</p>
      );
    case "permission-denied":
      return (
        <p className="audit-viewer__permission-denied" role="alert">
          {t("audit.state.permission_denied")}
        </p>
      );
    case "validation-error":
      return (
        <p className="audit-viewer__validation-error" role="alert">
          {error?.message ?? t("audit.state.validation_error")}
        </p>
      );
    case "fetch-failure":
      return (
        <p className="audit-viewer__fetch-failure" role="alert">
          {t("audit.state.fetch_failure")}
        </p>
      );
    case "rate-limit":
      return (
        <p className="audit-viewer__rate-limit" role="alert">
          {t("audit.state.rate_limit")}
        </p>
      );
    case "success":
      return (
        <section className="audit-viewer">
          <h1>{t("audit.title")}</h1>
          <AuditFilterBar filter={filter} onChange={setFilter} />
          <AuditViewerTable entries={data?.entries ?? []} />
          <AuditPagination
            filter={filter}
            hasMore={data?.pagination.has_more ?? false}
            onChange={setFilter}
          />
        </section>
      );
  }
}
